import { useState, type ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import { RebateAnalyticsCollapseToggle } from '@/components/rebates/RebateAnalyticsCollapseToggle';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { PurchasesVsRebatesChart } from './PurchasesVsRebatesChart';
import {
  defaultPurchaseTrendVisibleLines,
  type PurchaseTrendChartRow,
  type PurchaseTrendSubtitleModel,
} from './purchaseTrendChart';
import type {
  LastUploadCardModel,
  PurchaseChartStatus,
  PurchaseTrendTitleFilter,
  PurchaseTrendTitleModel,
} from './purchaseTrendPage';

function LastUploadCopy({ model }: { model: LastUploadCardModel }) {
  if (model.kind === 'session') {
    return (
      <p>
        <T context="last purchases upload details for the file in view">
          <Var>{model.fileName}</Var> · uploaded{' '}
          <Var>{model.uploadedAt.toLocaleDateString()}</Var> · through{' '}
          <Var>{model.throughLabel}</Var>
        </T>
      </p>
    );
  }
  if (model.kind === 'inferred') {
    return (
      <p>
        <T context="last purchases through-month inferred from trend data">
          Latest purchases through <Var>{model.throughLabel}</Var>.
        </T>
      </p>
    );
  }
  return (
    <p className="text-muted-foreground">
      <T context="empty last upload state">
        No purchases file has been imported for this year.
      </T>
    </p>
  );
}

function PurchaseTrendSubtitle({
  subtitle,
}: {
  subtitle: PurchaseTrendSubtitleModel;
}) {
  const parts: ReactNode[] = [];
  if (subtitle.lastActualMonthName) {
    parts.push(
      <T key="actuals" context="purchases chart subtitle actuals through month">
        Actuals through <Var>{subtitle.lastActualMonthName}</Var>
      </T>,
    );
  }
  if (subtitle.forecastFromName && subtitle.forecastToName) {
    parts.push(
      subtitle.forecastFromName === subtitle.forecastToName ? (
        <T
          key="forecast-one"
          context="purchases chart subtitle single forecast month"
        >
          forecast <Var>{subtitle.forecastFromName}</Var>
        </T>
      ) : (
        <T
          key="forecast-range"
          context="purchases chart subtitle forecast month range"
        >
          forecast <Var>{subtitle.forecastFromName}</Var> to{' '}
          <Var>{subtitle.forecastToName}</Var>
        </T>
      ),
    );
  }
  if (subtitle.includePriorYear) {
    parts.push(
      <T
        key="prior-year"
        context="purchases chart subtitle prior-year comparator"
      >
        prior-year comparator
      </T>,
    );
  }
  if (parts.length === 0) {
    return null;
  }
  return (
    <p
      className="text-sm text-muted-foreground"
      data-testid="purchase-trend-subtitle"
    >
      {parts.map((part, index) => (
        <span key={index}>
          {index > 0 ? ' · ' : null}
          {part}
        </span>
      ))}
    </p>
  );
}

function titleFilterText(
  filter: PurchaseTrendTitleFilter | null,
  countLabel: (count: number) => string,
): string | null {
  if (!filter) {
    return null;
  }
  if (filter.kind === 'count') {
    return countLabel(filter.count);
  }
  return filter.names.join(', ');
}

function PurchaseTrendChartTitle({
  title,
}: {
  title: PurchaseTrendTitleModel;
}) {
  const gt = useGT();
  const suppliers = titleFilterText(title.suppliers, (count) =>
    gt('{count} suppliers', {
      context:
        'purchases chart title when more than two suppliers are selected',
      count: String(count),
    }),
  );
  const divisions = titleFilterText(title.divisions, (count) =>
    gt('{count} divisions', {
      context:
        'purchases chart title when more than two divisions are selected',
      count: String(count),
    }),
  );
  return (
    <CardTitle data-testid="purchase-trend-title">
      {gt('Purchases and rebates{supplierPart}{divisionPart} – {year}', {
        context: 'purchases and rebate earnings trend chart title',
        supplierPart: suppliers ? ` – ${suppliers}` : '',
        divisionPart: divisions ? ` – ${divisions}` : '',
        year: String(title.year),
      })}
    </CardTitle>
  );
}

function PurchaseTrendChartBody({
  status,
  rows,
  reportedAlgorithm,
  visibleLines,
  onVisibleLinesChange,
}: {
  status: PurchaseChartStatus;
  rows: PurchaseTrendChartRow[];
  reportedAlgorithm: string | null;
  visibleLines: Set<string>;
  onVisibleLinesChange: (lines: Set<string>) => void;
}) {
  const gt = useGT();
  if (status === 'loading') {
    return (
      <div className="flex justify-center p-6">
        <div className="text-center">
          <Spinner size="lg" className="mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">
            {gt('Loading purchases and rebates...', {
              context: 'loading purchases versus rebates chart',
            })}
          </p>
        </div>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <p className="py-12 text-center text-destructive">
        <T context="error loading the purchases trend report">
          Error loading report data. Please try again.
        </T>
      </p>
    );
  }
  return (
    <PurchasesVsRebatesChart
      rows={rows}
      reportedAlgorithm={reportedAlgorithm}
      visibleLines={visibleLines}
      onVisibleLinesChange={onVisibleLinesChange}
    />
  );
}

export function PurchaseTrendChartSection({
  status,
  rows,
  title,
  subtitle,
  filters,
  reportedAlgorithm,
  supplierDataThrough,
  lastUploadCard,
}: {
  status: PurchaseChartStatus;
  rows: PurchaseTrendChartRow[];
  title: PurchaseTrendTitleModel;
  subtitle: PurchaseTrendSubtitleModel;
  filters: ReactNode;
  reportedAlgorithm: string | null;
  supplierDataThrough: string | null;
  lastUploadCard: LastUploadCardModel;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [visibleLines, setVisibleLines] = useState(
    defaultPurchaseTrendVisibleLines,
  );
  return (
    <Card data-testid="purchase-trend-chart-section">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <RebateAnalyticsCollapseToggle
                isCollapsed={collapsed}
                onToggle={() => setCollapsed((open) => !open)}
                testId="purchase-trend-chart-collapse"
              />
              <PurchaseTrendChartTitle title={title} />
            </div>
            <PurchaseTrendSubtitle subtitle={subtitle} />
            {supplierDataThrough ? (
              <p
                className="text-xs text-muted-foreground"
                data-testid="supplier-data-through"
              >
                <T context="note naming the latest month with supplier-reported data on the purchases chart">
                  Supplier data through <Var>{supplierDataThrough}</Var>
                </T>
              </p>
            ) : null}
            <p
              className="text-sm text-muted-foreground"
              data-testid="rebate-earnings-scope"
            >
              <T context="notice that rebate earnings only include suppliers with rebate agreements">
                Rebate earnings cover suppliers with rebate agreements only.
              </T>
            </p>
          </div>
          {filters}
        </div>
      </CardHeader>
      {collapsed ? null : (
        <CardContent>
          <PurchaseTrendChartBody
            status={status}
            rows={rows}
            reportedAlgorithm={reportedAlgorithm}
            visibleLines={visibleLines}
            onVisibleLinesChange={setVisibleLines}
          />
          <div className="mt-4 text-sm" data-testid="last-upload">
            <p className="mb-1 font-medium">
              <T context="card title for last purchases upload">Last upload</T>
            </p>
            <LastUploadCopy model={lastUploadCard} />
          </div>
        </CardContent>
      )}
    </Card>
  );
}
